import React, { useState, useEffect } from 'react';
import { 
  Trophy, 
  Users, 
  Target, 
  Award, 
  Filter, 
  ChevronDown, 
  Loader,
  AlertCircle,
  Star,
  ArrowUp,
  ArrowDown,
  BarChart3,
  TrendingUp,
  Zap
} from 'lucide-react';
import API from '../../api/API';
import './AStatistiche.css';

const AStatistiche = () => {
  const [edizioneSelezionata, setEdizioneSelezionata] = useState(null);
  const [statistiche, setStatistiche] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const storedEdition = localStorage.getItem('selectedEdition');
    if (storedEdition) {
      const edizione = JSON.parse(storedEdition);
      setEdizioneSelezionata(edizione);
      fetchStatistiche(edizione.id);
    }
  }, []);


  const fetchStatistiche = async (edizioneId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await API.statistiche.full(edizioneId);
      if (response.isSuccess) {
        setStatistiche(response.data);
      } else {
        setError(response.errorMsg || 'Errore nel caricamento delle statistiche');
        setStatistiche(null);
      }
    } catch (err) {
      setError(err.message || 'Errore nel caricamento delle statistiche');
      setStatistiche(null);
    } finally {
      setLoading(false);
    }
  };

  // Funzione helper per calcolare le partite per squadra (una sola volta per efficienza)
  const getPartitePerSquadra = () => {
    if (!statistiche?.partite || !statistiche?.squadre) return {};
    
    const partitePerSquadra = {};
    
    // Inizializza conteggio per ogni squadra
    statistiche.squadre.forEach(squadra => {
      partitePerSquadra[squadra.nome] = 0;
    });
    
    // Conta le partite per ogni squadra
    statistiche.partite.forEach(partita => {
      if (partita.squdra1 && partitePerSquadra.hasOwnProperty(partita.squdra1)) {
        partitePerSquadra[partita.squdra1]++;
      }
      if (partita.squdra2 && partitePerSquadra.hasOwnProperty(partita.squdra2)) {
        partitePerSquadra[partita.squdra2]++;
      }
    });
    
    return partitePerSquadra;
  };

  // Funzione helper per applicare il conteggio delle partite ai giocatori
  const enrichPlayersWithMatches = (players) => {
    const partitePerSquadra = getPartitePerSquadra();
    
    return players.map(player => ({
      ...player,
      partite: partitePerSquadra[player.squadra] || 0
    }));
  };

  // Categoria 1: Top 5 giocatori uomini per mete (decrescente) e partite (decrescente)
  const getTopMeteMaschi = () => {
    if (!statistiche?.giocatori) return [];
    
    const playersWithMatches = enrichPlayersWithMatches(statistiche.giocatori);
    
    return playersWithMatches
      .filter(g => ['M', 'm', 'maschio', 'MASCHIO', 'male', 'MALE'].includes(g.sesso))
      .sort((a, b) => {
        const meteA = a.mete || 0;
        const meteB = b.mete || 0;
        const partiteA = a.partite || 0;
        const partiteB = b.partite || 0;
        
        if (meteB !== meteA) return meteB - meteA; // Mete decrescente
        return partiteB - partiteA; // Partite decrescente
      })
      .slice(0, 5);
  };

  // Categoria 2: Top 5 giocatrici donne per mete (decrescente) e partite (crescente)
  const getTopMeteFemmine = () => {
    if (!statistiche?.giocatori) return [];
    
    const playersWithMatches = enrichPlayersWithMatches(statistiche.giocatori);
    
    return playersWithMatches
      .filter(g => ['F', 'f', 'femmina', 'FEMMINA', 'female', 'FEMALE'].includes(g.sesso))
      .sort((a, b) => {
        const meteA = a.mete || 0;
        const meteB = b.mete || 0;
        const partiteA = a.partite || 0;
        const partiteB = b.partite || 0;
        
        if (meteB !== meteA) return meteB - meteA; // Mete decrescente
        return partiteA - partiteB; // Partite crescente
      })
      .slice(0, 5);
  };

  // Categoria 3: Top 5 giocatori uomini per scalpi pieni (decrescente) e scalpi vuoti (crescente)
  const getTopScalpiPieniMaschi = () => {
    if (!statistiche?.giocatori) return [];
    
    const playersWithMatches = enrichPlayersWithMatches(statistiche.giocatori);
    
    return playersWithMatches
      .filter(g => ['M', 'm', 'maschio', 'MASCHIO', 'male', 'MALE'].includes(g.sesso))
      .sort((a, b) => {
        const pieniA = a.pieni || 0;
        const pieniB = b.pieni || 0;
        const vuotiA = a.vuoti || 0;
        const vuotiB = b.vuoti || 0;
        
        if (pieniB !== pieniA) return pieniB - pieniA; // Pieni decrescente
        return vuotiA - vuotiB; // Vuoti crescente
      })
      .slice(0, 5);
  };

  // Categoria 4: Top 5 giocatrici donne per scalpi pieni (decrescente) e scalpi vuoti (crescente)  
  const getTopScalpiPieniFemmine = () => {
    if (!statistiche?.giocatori) return [];
    
    const playersWithMatches = enrichPlayersWithMatches(statistiche.giocatori);
    
    return playersWithMatches
      .filter(g => ['F', 'f', 'femmina', 'FEMMINA', 'female', 'FEMALE'].includes(g.sesso))
      .sort((a, b) => {
        const pieniA = a.pieni || 0;
        const pieniB = b.pieni || 0;
        const vuotiA = a.vuoti || 0;
        const vuotiB = b.vuoti || 0;
        
        if (pieniB !== pieniA) return pieniB - pieniA; // Pieni decrescente
        return vuotiA - vuotiB; // Vuoti crescente
      })
      .slice(0, 5);
  };

  // Categoria 5: Top 5 giocatori uomini per assist (decrescente) e partite (crescente)
  const getTopAssistMaschi = () => {
    if (!statistiche?.giocatori) return [];
    
    const playersWithMatches = enrichPlayersWithMatches(statistiche.giocatori);
    
    return playersWithMatches
      .filter(g => ['M', 'm', 'maschio', 'MASCHIO', 'male', 'MALE'].includes(g.sesso))
      .sort((a, b) => {
        const assistA = a.assist || 0;
        const assistB = b.assist || 0;
        const partiteA = a.partite || 0;
        const partiteB = b.partite || 0;
        
        if (assistB !== assistA) return assistB - assistA; // Assist decrescente
        return partiteA - partiteB; // Partite crescente
      })
      .slice(0, 5);
  };

  // Categoria 6: Top 5 giocatrici donne per assist (decrescente) e partite (crescente)
  const getTopAssistFemmine = () => {
    if (!statistiche?.giocatori) return [];
    
    const playersWithMatches = enrichPlayersWithMatches(statistiche.giocatori);
    
    return playersWithMatches
      .filter(g => ['F', 'f', 'femmina', 'FEMMINA', 'female', 'FEMALE'].includes(g.sesso))
      .sort((a, b) => {
        const assistA = a.assist || 0;
        const assistB = b.assist || 0;
        const partiteA = a.partite || 0;
        const partiteB = b.partite || 0;
        
        if (assistB !== assistA) return assistB - assistA; // Assist decrescente
        return partiteA - partiteB; // Partite crescente
      })
      .slice(0, 5);
  };



  const AdminStatCard = ({ 
    title, 
    icon: Icon, 
    iconClass, 
    players, 
    primaryStat, 
    primaryLabel,
    secondaryStat, 
    secondaryLabel,
    primaryDesc,
    secondaryDesc 
  }) => (
    <div className={`admin-stat-card ${iconClass}`}>
      <div className="admin-stat-header">
        <div className="admin-stat-icon-container">
          <Icon className={`admin-stat-icon ${iconClass}`} />
        </div>
        <div className="admin-stat-title-container">
          <h3 className="admin-stat-title">{title}</h3>
          <div className="admin-stat-criteria">
            <span className="criteria-item">
              {primaryDesc} <ArrowDown className="sort-icon" />
            </span>
            <span className="criteria-divider">•</span>
            <span className="criteria-item">
              {secondaryDesc} {secondaryDesc.includes('crescente') ? 
                <ArrowUp className="sort-icon" /> : <ArrowDown className="sort-icon" />}
            </span>
          </div>
        </div>
      </div>
      
      <div className="admin-stat-list">
        {players && players.length > 0 ? (
          players.map((player, index) => (
            <div key={`${player.id || index}-${player.nome}-${player.cognome}`} className="admin-stat-item">
              <div className={`position-badge ${index < 3 ? `pos-${index + 1}` : 'pos-other'}`}>
                {index + 1}
              </div>
              
              <div className="player-info">
                <h4 className="player-name">
                  {player.nome} {player.cognome}
                </h4>
                <p className="player-team">
                  {player.squadra || player.maglia || 'N/A'}
                  {player.numero && ` • #${player.numero} ${player.maglia}`}
                </p>
              </div>
              
              <div className="player-stats">
                <div className="stat-primary">
                  <span className="stat-value">{player[primaryStat] || 0}</span>
                  <span className="stat-label">{primaryLabel}</span>
                </div>
                <div className="stat-secondary">
                  <span className="stat-value">{player[secondaryStat] || 0}</span>
                  <span className="stat-label">{secondaryLabel}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-players-data">
            <Users className="no-data-icon" />
            <span>Nessun giocatore trovato</span>
          </div>
        )}
      </div>
    </div>
  );

  const LoadingSpinner = () => (
    <div className="admin-loading">
      <Loader className="admin-loading-icon" />
      <h3 className="admin-loading-title">Caricamento statistiche...</h3>
      <p className="admin-loading-text">Attendere prego</p>
    </div>
  );

  const ErrorMessage = () => (
    <div className="admin-error">
      <AlertCircle className="admin-error-icon" />
      <h3 className="admin-error-title">Errore nel caricamento</h3>
      <p className="admin-error-text">{error}</p>
      {edizioneSelezionata && (
        <button 
          onClick={() => fetchStatistiche(edizioneSelezionata.id)}
          className="admin-retry-button"
        >
          Riprova
        </button>
      )}
    </div>
  );



  return (
    <div className="admin-container">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-content">
          <BarChart3 className="admin-header-icon" />
          <div>
            <h1 className="admin-title">Statistiche</h1>
            <p className="admin-subtitle">Analizza le performance dei giocatori per edizione</p>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage />
      ) : !edizioneSelezionata ? (
        <div className="admin-no-selection">
          <TrendingUp className="no-selection-icon" />
          <h3>Nessuna edizione selezionata</h3>
          <p>Seleziona un'edizione dalla pagina Tornei per visualizzare le statistiche dettagliate</p>
        </div>
      ) : !statistiche ? (
        <div className="admin-no-selection">
          <TrendingUp className="no-selection-icon" />
          <h3>Nessun dato disponibile</h3>
          <p>Non ci sono statistiche disponibili per questa edizione</p>
        </div>
      ) : (
        <>
          {/* Top Players Classifications */}
          <div className="admin-classifications">
            <h2 className="classifications-title">
              <Star className="classifications-icon" />
              Classifiche Giocatori - Top 5
            </h2>
            <div className="admin-stats-grid">
              {/* Categoria 1: Migliori realizzatori uomini */}
              <AdminStatCard
                title="Migliori Realizzatori"
                icon={Award}
                iconClass="realizzatori-m"
                players={getTopMeteMaschi()}
                primaryStat="mete"
                primaryLabel="mete"
                secondaryStat="partite"
                secondaryLabel="partite"
                primaryDesc="Mete decrescente"
                secondaryDesc="Partite decrescente"
              />

              {/* Categoria 2: Migliori realizzatrici donne */}
              <AdminStatCard
                title="Migliori Realizzatrici"
                icon={Award}
                iconClass="realizzatrici-f"
                players={getTopMeteFemmine()}
                primaryStat="mete"
                primaryLabel="mete"
                secondaryStat="partite"
                secondaryLabel="partite"
                primaryDesc="Mete decrescente"
                secondaryDesc="Partite crescente"
              />

              {/* Categoria 3: Migliori scalpatori uomini */}
              <AdminStatCard
                title="Migliori Scalpatori"
                icon={Target}
                iconClass="scalpatori-m"
                players={getTopScalpiPieniMaschi()}
                primaryStat="pieni"
                primaryLabel="pieni"
                secondaryStat="vuoti"
                secondaryLabel="vuoti"
                primaryDesc="Scalpi pieni decrescente"
                secondaryDesc="Scalpi vuoti crescente"
              />

              {/* Categoria 4: Migliori scalpatrici donne */}
              <AdminStatCard
                title="Migliori Scalpatrici"
                icon={Target}
                iconClass="scalpatrici-f"
                players={getTopScalpiPieniFemmine()}
                primaryStat="pieni"
                primaryLabel="pieni"
                secondaryStat="vuoti"
                secondaryLabel="vuoti"
                primaryDesc="Scalpi pieni decrescente"
                secondaryDesc="Scalpi vuoti crescente"
              />

              {/* Categoria 5: Migliori assistmen uomini */}
              <AdminStatCard
                title="Migliori Assistmen"
                icon={Zap}
                iconClass="assistmen-m"
                players={getTopAssistMaschi()}
                primaryStat="assist"
                primaryLabel="assist"
                secondaryStat="partite"
                secondaryLabel="partite"
                primaryDesc="Assist decrescente"
                secondaryDesc="Partite crescente"
              />

              {/* Categoria 6: Migliori assistwoman donne */}
              <AdminStatCard
                title="Migliori Assistwoman"
                icon={Zap}
                iconClass="assistwoman-f"
                players={getTopAssistFemmine()}
                primaryStat="assist"
                primaryLabel="assist"
                secondaryStat="partite"
                secondaryLabel="partite"
                primaryDesc="Assist decrescente"
                secondaryDesc="Partite crescente"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AStatistiche;